// Salespeople: types, a `use…()` hook and a picker every module can drop into a form.
//
// Typed into a text box, "JK", "Janis" and "J. Kalniņš" are three salespeople; picked from the
// list they are one, and the one whose login "my customers" recognises. The list is tenant-wide,
// so the picker needs no company and the same control serves a customer card, a supplier card
// (as the purchaser) and a sales order.
import { useApi } from "@keel/core";
import { Select } from "../Select";
import { Field } from "../ui";

export interface Salesperson {
  id: string;
  /** Upper-case; what cards and documents store. */
  code: string;
  name: string;
  email: string | null;
  phone: string | null;
  /** The login this salesperson is, if they have one — a member of this tenant. */
  userId: string | null;
  /** Per cent of net sales earned, 0–100. */
  commissionPct: number;
  /** Also offered on supplier cards as the purchaser responsible. */
  isPurchaser: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A member of this tenant a salesperson can be linked to. Needs `masterdata.setup.write`. */
export interface SalespersonLogin {
  userId: string;
  name: string;
  email: string;
}

/** Active people by default; the maintenance table asks for the ones who left too. */
export function useSalespeople(opts: { includeInactive?: boolean; purchasers?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.includeInactive) params.set("includeInactive", "true");
  if (opts.purchasers) params.set("purchasers", "true");
  const query = params.toString();
  return useApi<Salesperson[]>(query ? `/setup/salespeople?${query}` : "/setup/salespeople");
}

/** The members a salesperson can be linked to; `enabled: false` skips the request for readers. */
export function useSalespersonLogins(enabled = true) {
  return useApi<SalespersonLogin[]>(enabled ? "/setup/salespeople/logins" : null);
}

export interface SalespersonSelectProps {
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** Offer purchasers only — what a supplier card asks for. */
  purchasers?: boolean;
  /** Allow nobody at all, with this label. */
  emptyLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * The bare control. People who left stay in the list, greyed out: an old card still names them,
 * and a list where the rep you expected is simply missing sends people off to Settings to find
 * out why. Only non-purchasers are left out of a supplier card's list — unless the card already
 * names one, so opening it does not silently blank the field.
 */
export function SalespersonSelect({
  value,
  onChange,
  purchasers = false,
  emptyLabel,
  disabled,
  placeholder = "Choose a salesperson…",
}: SalespersonSelectProps) {
  const people = useSalespeople({ includeInactive: true }).data ?? [];
  const offered = people.filter((s) => !purchasers || s.isPurchaser || s.code === value);
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {offered.map((s) => (
        <option key={s.id} value={s.code} disabled={!s.isActive}>
          {s.code} - {s.name}
          {s.isActive ? "" : " (left)"}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and a hint that says what the chosen person earns on the sale. */
export function SalespersonField({
  label,
  hint,
  ...rest
}: SalespersonSelectProps & { label: string; hint?: string }) {
  const chosen = useSalespeople({ includeInactive: true }).data?.find((s) => s.code === rest.value);
  const effect = chosen
    ? chosen.commissionPct > 0
      ? `${chosen.name} earns ${chosen.commissionPct}% commission on this`
      : (chosen.email ?? hint)
    : hint;
  return (
    <Field label={label} hint={effect}>
      <SalespersonSelect {...rest} />
    </Field>
  );
}
